import { createTheme, rem } from '@mantine/core';
import { vdzGray, vdzOrange, vdzYellow } from './theme/colors';
import { REQUIRED_MARK_COLOR } from './components/form/requiredMark';
import tabsClasses from './components/ui/Tabs.module.css';
import inputClasses from './components/ui/Input.module.css';
import buttonClasses from './components/ui/Button.module.css';

export const theme = createTheme({
  primaryColor: 'vdzGray',
  primaryShade: 6,

  black: '#4A4A4A',

  colors: {
    vdzGray,
    vdzOrange,
    vdzYellow,
  },

  fontFamily: '"VDZ Visuelt", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  // fontFamilyMonospace intentionally omitted — Mantine's default monospace is fine
  // for the few places it's still needed (none after Phase A).

  // Globale Line-Height 1.3 für Body/Text-Komponenten.
  lineHeights: {
    xs: '1.3',
    sm: '1.3',
    md: '1.3',
    lg: '1.3',
    xl: '1.3',
  },

  headings: {
    fontFamily: '"VDZ Visuelt", -apple-system, sans-serif',
    fontWeight: '700',
    sizes: {
      h1: { fontSize: rem(34), lineHeight: '1.3' },
      h2: { fontSize: rem(26), lineHeight: '1.3' },
      h3: { fontSize: rem(22), lineHeight: '1.3' },
      h4: { fontSize: rem(18), lineHeight: '1.3' },
      h5: { fontSize: rem(16), lineHeight: '1.3' },
    },
  },

  radius: {
    xs: rem(4),
    sm: rem(6),
    md: rem(8),
    lg: rem(12),
    xl: rem(16),
  },
  defaultRadius: 'md',

  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 20px rgba(0, 0, 0, 0.08)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.10)',
  },

  components: {
    Stack: {
      defaultProps: { gap: 'xl' },
    },
    Group: {
      defaultProps: { gap: 'xl' },
    },
    Input: {
      classNames: { input: inputClasses.input },
    },
    Switch: {
      styles: { label: { color: 'var(--mantine-color-text)', opacity: 1 } },
    },
    Tooltip: {
      defaultProps: { arrowSize: 12 },
      styles: {
        tooltip: {
          backgroundColor: 'var(--mantine-color-white)',
          border: '1px solid var(--mantine-color-vdzGray-3)',
          borderRadius: 4,
          padding: '1rem',
          fontSize: rem(12),
          color: '#4A4A4A',
          textAlign: 'center',
          boxShadow: 'var(--mantine-shadow-sm)',
        },
        arrow: {
          backgroundColor: 'var(--mantine-color-white)',
          border: '1px solid var(--mantine-color-vdzGray-3)',
        },
      },
    },
    Button: {
      defaultProps: { size: 'sm' },
      classNames: {
        root: buttonClasses.root,
        label: buttonClasses.label,
        section: buttonClasses.section,
      },
    },
    TextInput: {
      defaultProps: { size: 'sm' },
    },
    NumberInput: {
      defaultProps: { size: 'sm' },
    },
    PasswordInput: {
      defaultProps: { size: 'sm' },
    },
    Select: {
      defaultProps: { size: 'sm' },
    },
    Paper: {
      defaultProps: { shadow: 'xs', withBorder: true },
      styles: { root: { borderColor: 'var(--mantine-color-vdzGray-2)' } },
    },
    Card: {
      defaultProps: { withBorder: true, radius: 'md' },
      styles: {
        root: {
          borderColor: 'var(--mantine-color-vdzGray-2)',
          paddingTop: rem(28),
          paddingBottom: rem(28),
        },
      },
    },
    Tabs: {
      classNames: {
        list: tabsClasses.list,
        tab: tabsClasses.tab,
        tabSection: tabsClasses.tabSection,
      },
    },
    InputWrapper: {
      styles: {
        label: { fontWeight: 700, fontSize: rem(14), paddingBottom: rem(3) },
        description: { color: 'var(--mantine-color-vdzGray-6)' },
        // Pflichtfeld-Sternchen hinter dem Label; Mantines Default waere red.6.
        required: { color: REQUIRED_MARK_COLOR },
      },
    },
    Table: {
      defaultProps: {
        // Setzt --table-vertical-spacing / --table-horizontal-spacing einmalig
        // auf der Table-Root → Zell-Padding ohne Inline-Style pro Zelle.
        // 0.75rem vertikal kompensiert die Line-Height (1.3); 11px horizontal.
        verticalSpacing: '0.75rem',
        horizontalSpacing: 11,
      },
      // Zentrales Styling der Tabellenköpfe (gilt für alle <Table.Th>).
      styles: {
        th: {
          fontSize: rem(12),
          fontWeight: 700,
          color: '#B9B6B0',
        },
      },
    },
  },
});
